/**
 * @file header.component.tsx
 * @description Компонент заголовка формы объекта для Task Manager приложения, который обеспечивает расширенный интерфейс управления формой с редактируемым заголовком, индикатором состояния и контекстным меню действий. Включает inline-редактирование названия через WrapperTextField, компонент состояния HeaderState с badge-индикатором, кнопки сохранения и отмены, контекстное меню с дополнительными действиями и поддержку навигационных хлебных крошек для иерархических объектов.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-01-15
 * @dependencies ['@components/ui/base/text', '@components/ui/base/button', '@consta/icons/IconClose', '@consta/icons/IconRevert', '@consta/icons/IconSave', '@consta/icons/IconKebab', 'classnames', '@src/types', '@consta/uikit/ContextMenu', 'react', './header-state.component', '@components/form-controls/wrapper-text-field']
 * @related-files ['src/features/object-form/object-form.component.tsx', 'src/features/object-form/components/header-state.component.tsx']
 */

import { Text } from '@components/ui/base/text';
import { Button } from '@components/ui/base/button';
// import { Breadcrumb } from '@components/ui/breadcrumb';
import { IconClose } from '@consta/icons/IconClose';
import { IconRevert } from '@consta/icons/IconRevert';
import { IconSave } from '@consta/icons/IconSave';
import { IconKebab } from '@consta/icons/IconKebab';
import cn from 'classnames';
import { TContextObject } from '@src/types';
import { ContextMenu } from '@consta/uikit/ContextMenu';
import { useMemo, useRef, useState } from 'react';
// import { TextField } from '@components/form-controls/text-field';
import { HeaderState } from './header-state.component';
import { WrapperTextField } from '@components/form-controls/wrapper-text-field';

interface IHeader {
    className?: string;
    control: any;
    item: any;
    setValue: any;
    container: any;
    number: string;
    titleAccessor?: string;
    isChanged: boolean;
    isLoading: boolean;
    isSimple?: boolean;
    closePageHandler: any;
    saveObjectHandler: Function;
    onPageRefresh: Function;
    contextMenuItems?: TContextObject[];
}

export const Header = ({
    className,
    control,
    container,
    titleAccessor = 'name',
    number,
    // breadcrumbs,
    closePageHandler,
    saveObjectHandler,
    isChanged,
    isLoading,
    contextMenuItems = [],
    onPageRefresh,
    isSimple = true
}: IHeader) => {
    const [isShowContext, setIsShowContext] = useState<boolean>(false);
    const contextMenuItemList = useMemo<TContextObject[]>(
        () => [
            {
                label: 'Обновить данные',
                icon: <IconRevert size="xs" className="text-[#83BCE7]" />,
                clickHandler: onPageRefresh
            },
            ...contextMenuItems
        ],
        [contextMenuItems]
    );
    const anchorRef = useRef(null);

    return (
        <div
            className={cn(
                'objectForm__header w-full flex justify-between items-center mb-[0.317vmin] pr-[3.1756vmin] gap-[2.113vmin]',
                className
            )}
        >
            <div className="flex flex-col gap-[1.2703vmin] w-full wrapper">
                <div className="objectForm__header-title-container flex gap-[0.8468vmin] items-center">
                    {!isSimple && <HeaderState control={control} container={container} />}

                    {!isSimple && number && (
                        <Text className="text-nowrap leading-[2.1171vmin]" size="m">
                            {number}
                        </Text>
                    )}

                    <WrapperTextField
                        className="cursor-pointer text-[1.6937vmin] font-semibold wrapper"
                        size="s"
                        control={control}
                        name={titleAccessor}
                        onFocus={(e: any) => e.target.select()}
                        view="clear"
                        placeholder="Введите наименование"
                        readOnly={isSimple}
                    />
                </div>
            </div>

            <div className="objectForm__header-controlPanel flex items-center gap-[0.8468vmin]">
                <Button
                    className="w-[19.0538vmin]"
                    onClick={(e) => saveObjectHandler(e, true)}
                    label="Сохранить и закрыть"
                    loading={isLoading}
                    disabled={!isChanged}
                />

                <Button
                    view="ghost"
                    loading={isLoading}
                    onlyIcon={true}
                    iconSize="xs"
                    iconLeft={IconSave}
                    disabled={!isChanged}
                    onClick={(e) => saveObjectHandler(e)}
                    label="Сохранить"
                />

                <div className="h-min-content flex items-top" ref={anchorRef}>
                    <Button
                        view="ghost"
                        onlyIcon={true}
                        iconLeft={IconKebab}
                        iconSize="xs"
                        onClick={() => setIsShowContext(true)}
                        label="Дополнительно"
                    />
                </div>

                {contextMenuItemList && (
                    <ContextMenu
                        items={contextMenuItemList}
                        isOpen={isShowContext}
                        anchorRef={anchorRef}
                        getItemLeftSide={(item) => item.icon}
                        onClickOutside={() => setIsShowContext(false)}
                        direction="downStartRight"
                        style={{ pointerEvents: 'auto' }}
                        getItemOnClick={(item) => {
                            setIsShowContext(false);
                            setTimeout(() => item.clickHandler(), 100);

                            return () => false;
                        }}
                        className="absolute"
                    />
                )}

                <Button
                    onlyIcon={true}
                    iconLeft={IconClose}
                    onClick={() => closePageHandler(false)}
                    label="Закрыть"
                    view="clear"
                    className="ml-[2.1171vmin]"
                />
            </div>
        </div>
    );
};
